import { api, type AuditFilters, type AuditPage, type AuditEvent } from '../api';
import { field, input, opt, select } from '../dialog';
import { errorCard, esc, fmtDate, html, loading, pill, tile } from '../ui';

/**
 * The audit trail, on a screen.
 *
 * It existed for a long time as rows nobody could read: thirteen files wrote to
 * it and nothing ever showed one, while the interface told people their changes
 * were "recorded in the audit log". This is the place that makes that true.
 *
 * Built for one question — "who changed this, and when" — so the trail reads as
 * sentences rather than as a table of JSON. Whatever the answer costs to find,
 * it is being looked for on a bad day: before an audit, after a surprise, or
 * when a number is not what somebody remembers leaving.
 */

const state: {
  filters: {
    entityType?: string;
    actorUserId?: string;
    action?: string;
    from?: string;
    to?: string;
    aircraftId?: string;
  };
  events: AuditEvent[];
  cursor: string | null;
  loadingMore: boolean;
} = { filters: {}, events: [], cursor: null, loadingMore: false };

const ACTION_TONE: Record<string, 'OK' | 'DUE_SOON' | 'OVERDUE' | 'NA'> = {
  CREATE: 'OK',
  UPDATE: 'DUE_SOON',
  DELETE: 'OVERDUE',
  SIGN: 'OK',
  EXPORT: 'NA',
  LOGIN: 'NA',
};

/** A field name as a person reads it: `invoiceNextNumber` → "Invoice next number". */
const fieldLabel = (field: string): string => {
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_.]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const when = (iso: string): string => {
  const d = new Date(iso);
  return `${fmtDate(iso)} ${d.toISOString().slice(11, 16)}`;
};

/** Long values are folded, because one pasted note should not hide a day of history. */
const value = (v: string | null): string =>
  v === null
    ? html`<span class="dim">—</span>`
    : v.length > 160
      ? html`<details><summary class="tiny">${esc(v.slice(0, 80))}…</summary><div class="tiny">${esc(v)}</div></details>`
      : esc(v);

function changeRows(e: AuditEvent): string {
  if (e.changes.length) {
    return html`
      <table class="tbl tiny" style="margin-top:6px">
        <thead><tr><th style="width:34%">Field</th><th style="width:33%">Was</th><th>Became</th></tr></thead>
        <tbody>
          ${e.changes
            .map(
              (c) => html`<tr>
                <td>${esc(fieldLabel(c.field))}</td>
                <td class="dim">${value(c.before)}</td>
                <td><b>${value(c.after)}</b></td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    `;
  }

  /**
   * An event rather than an edit — something created, deleted or exported.
   *
   * Both halves are shown when both exist, and the "what was there" half is
   * the one that matters on a deletion: it is the only description left of the
   * thing that is gone. An earlier version showed the "after" alone, so a
   * deleted organisation read "Deleted: true" and said nothing about what had
   * been deleted.
   */
  const block = (title: string, rows: Record<string, unknown> | null | undefined) =>
    !rows || !Object.keys(rows).length
      ? ''
      : html`
          <p class="tiny dim" style="margin:8px 0 2px">${esc(title)}</p>
          <table class="tbl tiny">
            <tbody>
              ${Object.entries(rows)
                .map(
                  ([k, v]) => html`<tr>
                    <td style="width:34%">${esc(fieldLabel(k))}</td>
                    <td>${value(v === null || v === undefined ? null : String(v))}</td>
                  </tr>`,
                )
                .join('')}
            </tbody>
          </table>
        `;

  const hasBefore = Boolean(e.recorded?.before && Object.keys(e.recorded.before).length);
  return (
    block(hasBefore ? 'What was there' : '', e.recorded?.before) +
    block(hasBefore ? 'What was recorded' : '', e.recorded?.after)
  );
}

function eventRow(e: AuditEvent): string {
  const count = e.changes.length;
  return html`
    <details class="card" style="margin:0 0 8px">
      <summary class="card-h" style="padding:10px 14px">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <b>${esc(e.entityLabel)} ${esc(e.actionLabel)}</b>
            <div class="tiny dim">
              ${esc(when(e.occurredAt))} · ${esc(e.actor)}${e.byPlatformAdmin
                ? ' · from the platform console'
                : ''}${count ? ` · ${count} field${count === 1 ? '' : 's'}` : ''}
            </div>
          </div>
        </div>
        ${pill(ACTION_TONE[e.action] ?? 'NA', e.actionLabel)}
      </summary>
      <div class="card-b" style="padding-top:0">
        ${changeRows(e)}
        <p class="tiny dim" style="margin:8px 0 0">
          ${esc(e.entityType)} <span class="mono">${esc(e.entityId)}</span>${e.ipAddress
            ? html` · from ${esc(e.ipAddress)}`
            : ''}
        </p>
      </div>
    </details>
  `;
}

function view(filters: AuditFilters, page: AuditPage): string {
  const f = state.filters;
  const total = filters.entityTypes.reduce((n, t) => n + t.count, 0);

  return html`
    <div class="phead">
      <div>
        <h1>Audit trail</h1>
        <p>
          Every change this organisation has recorded, newest first — what changed, who changed it and
          when. Nothing here can be edited or removed, by anyone.
        </p>
        ${
          /**
           * Said out loud when the list is narrowed to one aircraft.
           *
           * The tiles below count the organisation's whole trail and go on
           * doing so, which is right — but with one aircraft selected the page
           * would otherwise read "89 changes recorded" above a list of one, and
           * leave the reader to work out which number answers their question.
           */
          f.aircraftId
            ? html`<p class="tiny" style="margin-top:4px">
                <b>Showing ${esc(filters.aircraft.find((a) => a.value === f.aircraftId)?.label ?? 'one aircraft')}
                only.</b> The figures below count everything the organisation has recorded, not just this
                aircraft.
              </p>`
            : ''
        }
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Changes recorded', value: total })}
      ${tile({ label: 'Kinds of record', value: filters.entityTypes.length })}
      ${tile({ label: 'People', value: filters.actors.length })}
      ${tile({
        label: 'Goes back to',
        value: filters.earliest ? fmtDate(filters.earliest) : '—',
        sub: filters.earliest ? '' : 'nothing recorded yet',
      })}
    </div>

    <div class="card">
      <div class="card-b">
        <div class="frow3">
          ${field(
            'Kind of record',
            select(
              'entityType',
              [opt('', 'Everything', !f.entityType)]
                .concat(
                  filters.entityTypes.map((t) =>
                    opt(t.value, `${t.label} (${t.count})`, t.value === f.entityType),
                  ),
                )
                .join(''),
              'data-f="entityType"',
            ),
          )}
          ${field(
            'Who',
            select(
              'actorUserId',
              [opt('', 'Anybody', !f.actorUserId)]
                .concat(
                  filters.actors.map((a) => opt(a.value, `${a.label} (${a.count})`, a.value === f.actorUserId)),
                )
                .join(''),
              'data-f="actorUserId"',
            ),
          )}
          ${field(
            'What happened',
            select(
              'action',
              [opt('', 'Anything', !f.action)]
                .concat(
                  ['CREATE', 'UPDATE', 'DELETE', 'SIGN', 'EXPORT'].map((a) =>
                    opt(a, `${a.charAt(0)}${a.slice(1).toLowerCase()}`, a === f.action),
                  ),
                )
                .join(''),
              'data-f="action"',
            ),
          )}
        </div>
        <div class="frow3">
          ${field(
            'Aircraft',
            select(
              'aircraftId',
              [opt('', 'The whole organisation', !f.aircraftId)]
                .concat(
                  filters.aircraft.map((a) => opt(a.value, `${a.label} (${a.count})`, a.value === f.aircraftId)),
                )
                .join(''),
              'data-f="aircraftId"',
            ),
            'What happened to one aircraft’s records — the question asked at a review',
          )}
          ${field('From', input('from', f.from ?? '', 'type="date" data-f="from"'))}
          ${field('To', input('to', f.to ?? '', 'type="date" data-f="to"'))}
        </div>
      </div>
    </div>

    <div data-events style="margin-top:14px">
      ${page.events.length
        ? page.events.map(eventRow).join('')
        : html`<div class="card"><div class="card-b muted">
            Nothing matches that. ${Object.keys(f).length ? 'Try widening the filters.' : ''}
          </div></div>`}
    </div>

    <div style="text-align:center;margin:14px 0 30px">
      ${page.nextCursor
        ? html`<button class="btn" data-more>Show older changes</button>`
        : page.events.length
          ? html`<p class="tiny dim">That is the whole trail.</p>`
          : ''}
    </div>
  `;
}

export async function renderAudit(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Audit trail');

  /**
   * Arriving from an aircraft, rather than picking it out of the list.
   *
   * The filters live in module state so they survive moving around the screen,
   * which means a link carrying `?aircraft=` has to overwrite them — otherwise
   * following it from the second aircraft you looked at would quietly show you
   * the first. Read on every render for the same reason.
   */
  const wanted = new URLSearchParams(location.search).get('aircraft');
  if (wanted) state.filters.aircraftId = wanted;
  else delete state.filters.aircraftId;

  const query = () => ({ ...state.filters, take: 50 });

  const paint = (filters: AuditFilters, page: AuditPage) => {
    state.events = page.events;
    state.cursor = page.nextCursor;
    host.innerHTML = view(filters, page);
    bind(filters);
  };

  const bind = (filters: AuditFilters) => {
    host.querySelectorAll<HTMLSelectElement | HTMLInputElement>('[data-f]').forEach((el) =>
      el.addEventListener('change', () => {
        const key = el.dataset.f as keyof typeof state.filters;
        const v = el.value.trim();
        if (v) state.filters[key] = v;
        else delete state.filters[key];
        void api.audit.list(query()).then((page) => paint(filters, page));
      }),
    );

    host.querySelector<HTMLButtonElement>('[data-more]')?.addEventListener('click', async (ev) => {
      if (state.loadingMore) return;
      state.loadingMore = true;
      const button = ev.currentTarget as HTMLButtonElement;
      button.disabled = true;
      button.textContent = 'Reading…';
      try {
        // Appended rather than repainted: somebody five pages into a trail has
        // scrolled there deliberately, and starting them at the top again is
        // the fastest way to make a screen useless.
        const older = await api.audit.list({ ...query(), before: state.cursor ?? undefined });
        state.events = [...state.events, ...older.events];
        state.cursor = older.nextCursor;
        host.querySelector('[data-events]')!.innerHTML = state.events.map(eventRow).join('');
        if (!older.nextCursor) button.replaceWith(document.createRange().createContextualFragment(
          html`<p class="tiny dim">That is the whole trail.</p>`,
        ));
        else {
          button.disabled = false;
          button.textContent = 'Show older changes';
        }
      } finally {
        state.loadingMore = false;
      }
    });
  };

  try {
    const [filters, page] = await Promise.all([api.audit.filters(), api.audit.list(query())]);
    paint(filters, page);
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'The audit trail could not be read.');
  }
}
